import React, { useState, useEffect, useRef } from 'react';
import { apiClient } from '../../api/apiConfig';
import { UserCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { COLORS, SPACING, TYPOGRAPHY, RADIUS } from '../../design/tokens';

interface Person {
  id: number;
  name: string;
  relationship?: string;
}

interface PersonAutocompleteProps {
  selectedPersonIds: number[];
  onChange: (selectedPersonIds: number[]) => void;
  placeholder?: string;
  label?: string;
  style?: React.CSSProperties;
  /** When true, only allows selecting one person (replaces previous selection) */
  singleSelect?: boolean;
}

const PersonAutocomplete: React.FC<PersonAutocompleteProps> = ({
  selectedPersonIds,
  onChange,
  placeholder = "Type to search people...",
  label,
  style = {},
  singleSelect = false,
}) => {
  const [people, setPeople] = useState<Person[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get selected people objects
  const selectedPeople = people.filter(person => selectedPersonIds.includes(person.id));

  useEffect(() => {
    const fetchPeople = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get('/api/people/');
        setPeople(response.data);
      } catch (error) {
        console.error('Error fetching people:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPeople();
  }, []);

  // Filter people based on search and exclude already selected
  const filteredPeople = people.filter(person => {
    const matchesSearch = searchValue.length === 0 ||
      person.name.toLowerCase().includes(searchValue.toLowerCase());
    const notSelected = !selectedPersonIds.includes(person.id);
    return matchesSearch && notSelected;
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setShowDropdown(true); // Always show dropdown when typing
    setSelectedIndex(0);
  };

  const handleInputFocus = () => {
    setShowDropdown(true); // Show dropdown on focus, even with empty search
  };

  const handleInputBlur = () => {
    // Delay hiding dropdown to allow for click selection
    setTimeout(() => setShowDropdown(false), 150);
  };

  const selectPerson = (person: Person) => {
    // In single-select mode, replace the selection; otherwise append
    const newSelectedIds = singleSelect ? [person.id] : [...selectedPersonIds, person.id];
    onChange(newSelectedIds);
    setSearchValue('');
    setShowDropdown(false);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const removePerson = (personId: number) => {
    const newSelectedIds = selectedPersonIds.filter(id => id !== personId);
    onChange(newSelectedIds);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (showDropdown && filteredPeople.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < filteredPeople.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : prev);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredPeople[selectedIndex]) {
          selectPerson(filteredPeople[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        setShowDropdown(false);
        setSearchValue('');
      }
    }
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: TYPOGRAPHY.size.sm,
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.text.secondary,
    marginBottom: SPACING[4],
  };

  const tagsContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: SPACING[8],
    marginBottom: SPACING[8],
  };

  const tagStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    paddingLeft: SPACING[8],
    paddingRight: SPACING[8],
    paddingTop: SPACING[4],
    paddingBottom: SPACING[4],
    borderRadius: '9999px',
    fontSize: TYPOGRAPHY.size.xs,
    fontWeight: TYPOGRAPHY.weight.medium,
    backgroundColor: '#DBEAFE',
    color: '#1E40AF',
  };

  const tagIconStyle: React.CSSProperties = {
    width: '12px',
    height: '12px',
    marginRight: SPACING[4],
  };

  const removeButtonStyle: React.CSSProperties = {
    marginLeft: SPACING[4],
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '12px',
    height: '12px',
    color: '#60A5FA',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
  };

  const inputContainerStyle: React.CSSProperties = {
    position: 'relative',
  };

  const inputStyle: React.CSSProperties = {
    display: 'block',
    width: '100%',
    borderRadius: RADIUS,
    border: `1px solid ${COLORS.border.default}`,
    padding: `${SPACING[8]} ${SPACING[12]}`,
    fontSize: TYPOGRAPHY.size.sm,
    fontFamily: TYPOGRAPHY.body.fontFamily,
    backgroundColor: COLORS.bg.panel,
    color: COLORS.text.primary,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const dropdownStyle: React.CSSProperties = {
    position: 'absolute',
    zIndex: 10,
    marginTop: SPACING[4],
    width: '100%',
    borderRadius: RADIUS,
    backgroundColor: COLORS.bg.panel,
    border: `1px solid ${COLORS.border.default}`,
    maxHeight: '240px',
    overflowY: 'auto',
  };

  const loadingMessageStyle: React.CSSProperties = {
    padding: `${SPACING[8]} ${SPACING[16]}`,
    fontSize: TYPOGRAPHY.size.sm,
    color: COLORS.text.secondary,
  };

  const getDropdownItemStyle = (index: number): React.CSSProperties => ({
    width: '100%',
    padding: `${SPACING[8]} ${SPACING[16]}`,
    textAlign: 'left',
    fontSize: TYPOGRAPHY.size.sm,
    display: 'flex',
    alignItems: 'center',
    gap: SPACING[8],
    backgroundColor: index === selectedIndex || index === hoveredIndex ? COLORS.bg.hover : 'transparent',
    border: 'none',
    cursor: 'pointer',
  });

  const dropdownIconStyle: React.CSSProperties = {
    width: '20px',
    height: '20px',
    color: COLORS.text.tertiary,
  };

  const personNameStyle: React.CSSProperties = {
    fontWeight: TYPOGRAPHY.weight.medium,
    color: COLORS.text.primary,
  };

  const personRelationshipStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.size.xs,
    color: COLORS.text.secondary,
  };

  const noResultsStyle: React.CSSProperties = {
    padding: `${SPACING[8]} ${SPACING[16]}`,
    fontSize: TYPOGRAPHY.size.sm,
    color: COLORS.text.secondary,
  };

  const debugInfoStyle: React.CSSProperties = {
    fontSize: TYPOGRAPHY.size.xs,
    marginTop: SPACING[4],
    color: COLORS.text.tertiary,
  };

  return (
    <div style={style}>
      {label && (
        <label style={labelStyle}>
          {label}
        </label>
      )}

      {/* Selected people tags */}
      {selectedPeople.length > 0 && (
        <div style={tagsContainerStyle}>
          {selectedPeople.map(person => (
            <span
              key={person.id}
              style={tagStyle}
            >
              <UserCircleIcon style={tagIconStyle} />
              {person.name}
              <button
                type="button"
                onClick={() => removePerson(person.id)}
                style={removeButtonStyle}
              >
                <XMarkIcon style={{ width: '12px', height: '12px' }} />
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Search input */}
      <div style={inputContainerStyle}>
        <input
          ref={inputRef}
          type="text"
          value={searchValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          style={inputStyle}
        />

        {/* Dropdown */}
        {showDropdown && (
          <div
            ref={dropdownRef}
            style={dropdownStyle}
          >
            {isLoading ? (
              <div style={loadingMessageStyle}>Loading people...</div>
            ) : filteredPeople.length > 0 ? (
              filteredPeople.map((person, index) => (
                <button
                  key={person.id}
                  type="button"
                  style={getDropdownItemStyle(index)}
                  onClick={() => selectPerson(person)}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  <UserCircleIcon style={dropdownIconStyle} />
                  <div>
                    <div style={personNameStyle}>{person.name}</div>
                    {person.relationship && (
                      <div style={personRelationshipStyle}>{person.relationship}</div>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div style={noResultsStyle}>
                {searchValue ? `No people found matching "${searchValue}"` : 'No people available'}
                {/* Debug info */}
                <div style={debugInfoStyle}>
                  Total people: {people.length}, Filtered: {filteredPeople.length}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonAutocomplete;
